import { useState } from "react";
import { Sun, Moon, Menu, PenIcon, UserRoundIcon, AlertTriangle } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "./i18n/LangContext";

function LangToggle({ className = "" }) {
  const { lang, setLang, t } = useLang();
  const [showTip, setShowTip] = useState(false);
  const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  return (
    <div className={`relative ${className}`}>
      <div
        role="group"
        aria-label={t("header.language")}
        className="flex items-center rounded-md ring-1 ring-slate-200 dark:ring-slate-800 overflow-hidden text-xs font-semibold"
      >
        <button
          onClick={() => setLang("es")}
          aria-pressed={lang === "es"}
          className={`px-2 py-2 transition-colors ${
            lang === "es"
              ? "bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          }`}
        >
          ES
        </button>

        <button
          onClick={() => setLang("en")}
          onMouseEnter={() => canHover && setShowTip(true)}
          onMouseLeave={() => setShowTip(false)}
          onFocus={() => canHover && setShowTip(true)}
          onBlur={() => setShowTip(false)}
          aria-pressed={lang === "en"}
          className={`px-2 py-2 transition-colors ${
            lang === "en"
              ? "bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          }`}
        >
          EN
        </button>
      </div>

      {showTip && (
        <div className="absolute right-0 top-full mt-2 w-56 rounded-lg p-3 text-xs leading-snug font-normal text-left bg-amber-50 text-amber-900 border border-amber-300 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800 shadow-lg z-50">
          <div className="flex gap-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5 text-amber-500 dark:text-amber-400" />
            <span>
              The text was translated into English using AI, so some nuance or accuracy from the original may have been lost.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Header({ dark, setDark }) {
  const { t, localePath } = useLang();
  const [open, setOpen] = useState(false);
  const [touchStartX, setTouchStartX] = useState(0);

  const handleTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    if (touchEndX - touchStartX > 50) setOpen(false);
  };

  return (
    <header className="sm:sticky sm:top-0 z-50 w-full bg-white dark:bg-black sm:shadow-md">
    <div className="max-w-4xl mx-auto px-4 sm:px-5 flex items-center justify-between py-6">
      <div className="flex items-center gap-3 sm:gap-4 mr-3 sm:mr-6 min-w-0">
        <Logo className="flex-shrink-0" />
        <div className="hidden sm:block min-w-0">
          <h1 className="text-xl font-semibold truncate">Jaime Gimillo Bonaque</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
            {t("header.subtitle")}
          </p>
        </div>
        <div className="block sm:hidden min-w-0">
          <h1 className="text-xl font-semibold truncate">Jaime Gimillo</h1>
          <p className="text-xs min-[360px]:text-sm text-slate-500 dark:text-slate-400 truncate">
            {t("header.subtitleShort")}
          </p>
        </div>
      </div>

      {/* Menú desktop */}
      <nav className="hidden sm:flex items-center gap-4">
        <a href={localePath("/")} className="text-md hover:underline">About</a>
        <a href={localePath("/blog")} className="text-md hover:underline">Blog</a>
        <LangToggle className="ml-4" />
        <button
          aria-label="Toggle color mode"
          onClick={() => setDark((s) => !s)}
          className="p-2 rounded-md ring-1 ring-slate-200 dark:ring-slate-800"
        >
          {dark ? <Sun size={16} /> : <Moon size={16} />}
        </button>
      </nav>

      {/* Botón móvil */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="sm:hidden shrink-0 p-2 rounded-md ring-1 ring-slate-200 dark:ring-slate-800"
      >
        <Menu size={20} />
      </button>

      {/* Overlay menú móvil */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-50 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Menú móvil */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-white dark:bg-black shadow-lg p-6 flex flex-col gap-4 md:hidden transform transition-transform duration-300 z-50 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="inline-flex items-center">
          <UserRoundIcon size={16} className="mr-2"/>
          <a href={localePath("/")} className="text-md hover:underline" onClick={() => setOpen(false)}>About</a>
        </div>
        <div className="inline-flex items-center">
          <PenIcon size={16} className="mr-2"/>
          <a href={localePath("/blog")} className="text-md hover:underline" onClick={() => setOpen(false)}>Blog</a>
        </div>

        <div className="mt-2 flex items-center gap-3 self-start">
          <LangToggle />
          <button
            aria-label="Toggle color mode"
            onClick={() => {
              setDark((s) => !s);
              setOpen(false);
            }}
            className="p-2 rounded-md ring-1 ring-slate-200 dark:ring-slate-800"
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </div>
    </div>
    </header>
  );
}
